import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useModal } from '@/providers/modal';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useGetMe } from '@/hooks/useGetMe';
import { useReCaptcha } from '@/providers/recaptcha';
import { useUpdatePhoneNumber } from '@/query/queries/user/useUpdatePhoneNumber';
import { useSendPhoneVerifyCode } from '@/query/queries/user/useSendPhoneVerifyCode';
import { SuccessMessage } from '@/modals/TwoFactorAuthModal/SuccessMessage';
import { QUERY_KEYS } from '@/query/queryKeys';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { PhoneForm } from '../../modals/TwoFactorAuthModal/PhoneForm';
import { CodeSubmitForm } from '../../modals/TwoFactorAuthModal/CodeSubmitForm';

const id = MODAL_IDS.two_factor_auth_modal;

export const TwoFactorAuthModal = () => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal />;
};

const Modal = () => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const { user } = useGetMe();
  const [showSuccess, setShowSuccess] = useState(false);
  const queryClient = useQueryClient();
  const { token: captchaToken, loaded: captchaLoaded, getCaptchaToken } = useReCaptcha();
  const { mutateAsync: sendCode } = useSendPhoneVerifyCode(phone);
  const { mutateAsync: updatePhoneNumber } = useUpdatePhoneNumber();

  const { isMobile } = useWindowSize();
  const { closeModal } = useModal();

  const onSubmit = async values => {
    if (!captchaLoaded || loading) return;

    try {
      setLoading(true);
      const token = captchaToken || (await getCaptchaToken());

      if (!token) return;

      let currentPhone = values.phone;

      if (user.phone !== currentPhone) {
        const res = await updatePhoneNumber({
          phone_number: values.phone,
        });
        if (res?.phone) {
          currentPhone = res?.phone;
        }
      }

      if (!currentPhone) return;

      await sendCode({
        phone_number: currentPhone,
        recaptcha_token: token,
      });

      setPhone(currentPhone);
    } catch (e) {
      console.error('[Submit] Phone set error', e.message);
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    showSuccess &&
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.user.profile],
      });
    closeModal(id);
  };

  const disabled = !captchaLoaded || loading;

  const content = showSuccess ? (
    <SuccessMessage onClose={onClose} />
  ) : phone ? (
    <CodeSubmitForm
      phone={phone}
      onBack={() => setPhone('')}
      onSuccess={() => setShowSuccess(true)}
      disabled={disabled}
    />
  ) : (
    <PhoneForm loading={loading} onSubmit={onSubmit} disabled={disabled} />
  );

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={onClose} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={onClose} showClose>
      {content}
    </ModalCenterLayout>
  );
};
